import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  CircularProgress,
} from '@mui/material'
import { theme } from '../../../theme/DashboardTheme'
import { API, Auth } from 'aws-amplify'
import OptOutConfirmationModal from '../../shared/molecule/OptOutConfirmationModal'
import { useOutletContext } from 'react-router-dom'
import { Toast } from '../../toast/toast'

const styles = {
  card: { borderRadius: 3, background: '#f6f6f6' },
  preferenceCard: {
    borderRadius: 3,
    background: '#fff',
    marginBottom: 2,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: 'none',
  },
  selectedCard: {
    borderRadius: 3,
    background: '#fff',
    marginBottom: 2,
    cursor: 'pointer',
    border: '2px solid #9135E0',
    boxShadow: 'none',
  },
}

interface Preference {
  id: string
  title: string
  description: string
  disclaimer?: string
}

const preferences: Preference[] = [
  {
    id: 'all',
    title: 'I would like to receive all communications from Priwall by mePrism.',
    description:
      "You'll receive all product updates, security alerts, service notifications, and occasional announcements related to your account and privacy protection.",
  },
  {
    id: 'monthly',
    title: 'I would like to receive monthly updates and pause all other email communications.',
    description:
      "You'll receive a single monthly email with important updates and product improvements. All non-essential emails, promotions, and announcements will be paused.",
  },
  {
    id: 'none',
    title: 'I would like to opt out of all email communications from Priwall by mePrism.',
    description: "You won't receive any marketing or update emails.",
    disclaimer:
      'Critical account or legal notices may still be sent when required for service operation or compliance.',
  },
]

interface CommunicationPreferenceProps {
  registerUpdateFunction?: (updateFn: () => Promise<void>) => void
  onStateChange?: (state: { hasChanged: boolean; updating: boolean }) => void
}

interface OutletContext {
  registerUpdateFunction?: (updateFn: () => Promise<void>) => void
  onStateChange?: (state: { hasChanged: boolean; updating: boolean }) => void
}

const CommunicationPreference = ({ registerUpdateFunction: propRegisterUpdateFunction, onStateChange: propOnStateChange }: CommunicationPreferenceProps) => {
  const outletContext = useOutletContext<OutletContext>()
  const registerUpdateFunction = propRegisterUpdateFunction || outletContext?.registerUpdateFunction
  const onStateChange = propOnStateChange || outletContext?.onStateChange
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedPreference, setSelectedPreference] = useState<string>('all')
  const [initialPreference, setInitialPreference] = useState<string>('all')
  const [pendingPreference, setPendingPreference] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [updating, setUpdating] = useState<boolean>(false)
  const [showOptOutConfirmation, setShowOptOutConfirmation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<'none' | 'monthly'>('none')
  const [userEmail, setUserEmail] = useState<string>('')

  // Handle updating user preferences (used by button for none -> monthly)
  const handleUpdatePreferences = useCallback(async () => {
    try {
      setUpdating(true)
      await API.post('Email', '/set-user-email-preferences', {
        body: {
          email: userEmail,
          preference: selectedPreference
        }
      })
      setInitialPreference(selectedPreference)
      Toast.show({ type: 'success', text1: 'Preferences updated', text2: 'Your communication preferences have been saved.' })
    } catch (error) {
      console.error('Error updating preferences:', error)
      Toast.show({ type: 'error', text1: 'Update failed', text2: 'Something went wrong. Please try again.' })
    } finally {
      setUpdating(false)
    }
  }, [userEmail, selectedPreference])

  // Fetch user preferences on component mount
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        setLoading(true)

        const user = await Auth.currentAuthenticatedUser()
        const email = user.attributes.email
        const session = await Auth.currentSession()
        const token = session.getIdToken().getJwtToken()
        setUserEmail(email)

        const apiEndpoint = await API.endpoint('Email')

        const fetchResponse = await fetch(`${apiEndpoint}/user-email-preferences?email=${encodeURIComponent(email)}`, {
          method: 'GET',
          headers: {
            'Authorization': token
          }
        })

        if (!fetchResponse.ok) {
          throw new Error(`HTTP error! status: ${fetchResponse.status}`)
        }

        const response = await fetchResponse.json()

        if (response && response.preference) {
          setSelectedPreference(response.preference)
          setInitialPreference(response.preference)
        }
      } catch (error) {
        console.error('Error fetching preferences:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPreferences()
  }, [])

  const hasChanged = selectedPreference !== initialPreference

  // Register update function with parent component
  useEffect(() => {
    if (registerUpdateFunction) {
      registerUpdateFunction(handleUpdatePreferences)
    }
  }, [registerUpdateFunction, handleUpdatePreferences])

  // Notify parent of state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange({ hasChanged, updating })
    }
  }, [onStateChange, hasChanged, updating])

  const handlePreferenceChange = (preferenceId: string) => {
    // Don't allow selecting "all" if user has opted down to monthly or none
    if (preferenceId === 'all' && (selectedPreference === 'monthly' || selectedPreference === 'none')) {
      return
    }

    // Show confirmation for: all->monthly, all->none, monthly->none (only if DB value is monthly)
    // none->monthly goes directly (saved via "Update your Preferences" button)
    // If reverting an unsaved change back to the DB value, no modal needed
    const needsConfirmation =
      (selectedPreference === 'all' && (preferenceId === 'monthly' || preferenceId === 'none')) ||
      (selectedPreference === 'monthly' && preferenceId === 'none' && initialPreference === 'monthly')

    if (needsConfirmation) {
      setPendingPreference(preferenceId)
      setModalType(preferenceId as 'none' | 'monthly')
      setShowOptOutConfirmation(true)
      return
    }

    setSelectedPreference(preferenceId)
  }

  const handleConfirmOptOut = async () => {
    if (pendingPreference) {
      setSelectedPreference(pendingPreference)
      setPendingPreference(null)
      setShowOptOutConfirmation(false)

      try {
        setUpdating(true)
        await API.post('Email', '/set-user-email-preferences', {
          body: {
            email: userEmail,
            preference: pendingPreference
          }
        })
        setInitialPreference(pendingPreference)
        Toast.show({ type: 'success', text1: 'Preferences updated', text2: 'Your communication preferences have been saved.' })
      } catch (error) {
        console.error('Error updating preferences:', error)
        Toast.show({ type: 'error', text1: 'Update failed', text2: 'Something went wrong. Please try again.' })
      } finally {
        setUpdating(false)
      }
    } else {
      setShowOptOutConfirmation(false)
    }
  }

  const handleCancelOptOut = () => {
    setPendingPreference(null)
    setShowOptOutConfirmation(false)
  }

  const mainComponent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress sx={{ color: '#9135E0' }} />
        </Box>
      )
    }

    return (
      <Box>
        <Box sx={{ gap: 2 }}>
          {preferences.map((preference) => {
            const isDisabled = preference.id === 'all' && (selectedPreference === 'monthly' || selectedPreference === 'none')
            return (
              <Card
                key={preference.id}
                sx={{
                  ...(selectedPreference === preference.id
                    ? styles.selectedCard
                    : styles.preferenceCard),
                  opacity: isDisabled ? 0.5 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
                onClick={() => !isDisabled && handlePreferenceChange(preference.id)}>
                <CardContent
                sx={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  '&:last-child': {
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  },
                  '& *': {
                    cursor: isDisabled ? 'not-allowed !important' : 'pointer',
                  },
                }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedPreference === preference.id}
                        disabled={isDisabled}
                        sx={{
                          color: '#9135E0',
                          '&.Mui-checked': {
                            color: '#9135E0',
                          },
                          transform: 'scale(0.75)',
                        }}
                      />
                    }
                    sx={{
                      alignItems: 'flex-start',
                      m: 0,
                      width: '100%',
                    }}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: { xs: '16px', sm: '18px', md: '20px' },
                            fontWeight: 500,
                            color: isDisabled ? '#999' : '#3A155A',
                            lineHeight: '141%',
                            letterSpacing: '1%',
                            mb: 1,
                          }}>
                          {preference.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: { xs: '14px', sm: '16px', md: '18px' },
                            fontWeight: 400,
                            color: isDisabled ? '#999' : '#6B7280',
                            display: 'block',
                          }}>
                          {preference.description}
                        </Typography>
                        {preference.disclaimer && (
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: { xs: '14px', sm: '16px', md: '18px' },
                              fontWeight: 400,
                              color: isDisabled ? '#999' : '#6B7280',
                              display: 'block',
                              mt: '20px',
                            }}>
                            {preference.disclaimer}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </CardContent>
              </Card>
            )
          })}
        </Box>
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              disabled={!hasChanged || updating}
              onClick={handleUpdatePreferences}
              sx={{
                width: '100%',
                maxWidth: '279px',
                height: { xs: '40px', sm: '50px' },
                paddingTop: '16px',
                paddingRight: '50px',
                paddingBottom: '16px',
                paddingLeft: '50px',
                backgroundColor: '#9135E0 !important',
                borderRadius: '10px',
                color: '#fff',
                fontFamily: 'InterMedium',
                '&:hover': {
                  backgroundColor: '#7a2bbf !important',
                },
                '&:disabled': {
                  backgroundColor: '#9135E0 !important',
                  color: '#fff',
                  opacity: 0.5,
                  pointerEvents: 'none',
                },
              }}>
              {updating ? 'Updating...' : 'Update your Preferences'}
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <>
      {isMobile ? (
        mainComponent()
      ) : (
        <Card sx={{ borderRadius: 5, overflow: 'auto', boxShadow: 'none', background: 'transparent' }}>
          <CardContent sx={{ paddingLeft: 0, paddingRight: 0 }}>{mainComponent()}</CardContent>
        </Card>
      )}

      <OptOutConfirmationModal
        open={showOptOutConfirmation}
        onConfirm={handleConfirmOptOut}
        onCancel={handleCancelOptOut}
        type={modalType}
      />
    </>
  )
}

export default CommunicationPreference
